/**
 * <cart-upsell-full> — lazy-loads product recommendations into cart upsell swiper.
 * Loaded from `snippets/cart_upsell_full.liquid`.
 *
 * Swiper is initialized directly after fetch (async DOM — same exception as cart-drawer upsell).
 */

import Swiper from '@theme/swiper';
import { responsive } from '@theme/responsive';

const MAX_VISIBLE_DESKTOP_SLIDES = 4;
const MAX_API_LIMIT = 10;

function getUpsellSlideCount(container) {
  const swiperEl = container.querySelector('swiper-init');
  return swiperEl?.querySelectorAll(':scope > .swiper-wrapper > .swiper-slide').length ?? 0;
}

function syncCartUpsellNavigation(container) {
  if (!container.isConnected) return;

  const pagination = container.querySelector('.cart-upsell__swiper-pagination');
  if (!pagination) return;

  const slideCount = getUpsellSlideCount(container);
  const hideArrows = slideCount <= MAX_VISIBLE_DESKTOP_SLIDES;

  container.toggleAttribute('data-hide-arrows', hideArrows);

  pagination
    .querySelectorAll('.product-slider-arrow')
    .forEach((arrow) => arrow.classList.toggle('is-hidden', hideArrows));
}

function bindCartUpsellNavigationSync(container) {
  const swiperEl = container.querySelector('swiper-init');
  if (!swiperEl || container.dataset.upsellNavBound === 'true') return;

  const sync = () => syncCartUpsellNavigation(container);

  const bind = () => {
    const swiper = swiperEl.swiper;
    if (!swiper) return false;

    container.dataset.upsellNavBound = 'true';

    sync();
    swiper.on('resize', sync);
    swiper.on('update', sync);
    swiper.on('transitionEnd', sync);
    return true;
  };

  if (!container.dataset.upsellBreakpointBound) {
    container.dataset.upsellBreakpointBound = 'true';
    responsive.addBreakpointListener('lap_and_up');
    document.body.addEventListener('breakpoint:lap_and_up', sync);
  }

  if (bind()) return;

  swiperEl.addEventListener('swiper:initialized', () => bind(), { once: true });
}

class CartUpsellFull extends HTMLElement {
  #abortController = null;
  #swiperEl = null;
  #wrapper = null;
  #refreshGeneration = 0;
  #slideCache = new Map();

  connectedCallback() {
    this.#abortController = new AbortController();
    this.#swiperEl = this.querySelector('swiper-init');
    this.#wrapper = this.querySelector('.swiper-wrapper');
    if (!this.dataset.url || !this.#swiperEl || !this.#wrapper) return;

    this.#prepareForLoading();
    this.#load();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#swiperEl?.swiper?.destroy?.(true, true);
  }

  #prepareForLoading() {
    this.setAttribute('data-loaded', 'true');
    this.style.opacity = '0';
    this.style.visibility = 'hidden';
    this.style.pointerEvents = 'none';
  }

  #revealAfterLoad() {
    this.style.visibility = 'visible';
    this.style.pointerEvents = '';
    this.style.opacity = '1';
    this.removeAttribute('data-loaded');
  }

  #getProductIds() {
    try {
      const parsed = JSON.parse(this.dataset.productIds || '[]');
      if (!Array.isArray(parsed)) return [];
      return [
        ...new Set(
          parsed.map((id) => parseInt(id, 10)).filter((id) => Number.isInteger(id) && id > 0)
        ),
      ];
    } catch {
      return [];
    }
  }

  #getDisplayLimit() {
    const limit = parseInt(this.dataset.limit || '4', 10);
    return Number.isInteger(limit) && limit > 0 ? limit : 4;
  }

  #getApiLimit() {
    return MAX_API_LIMIT;
  }

  #buildRecommendationsUrl(productId) {
    if (!this.dataset.recommendationsUrl || !productId) return '';

    const params = new URLSearchParams({
      section_id: this.dataset.sectionId || 'cart-upsell_full',
      product_id: String(productId),
      limit: String(this.#getApiLimit()),
    });

    if (this.dataset.intent) {
      params.set('intent', this.dataset.intent);
    }

    return `${this.dataset.recommendationsUrl}?${params.toString()}`;
  }

  #getSlidesRoot(temp) {
    return (
      temp.querySelector('.cart-upsell--full__slides') ||
      temp.querySelector('[data-section-id]') ||
      temp.querySelector('section') ||
      temp
    );
  }

  #getSlideMarkup(root) {
    const wrapper = root.querySelector('.swiper-wrapper');
    if (wrapper) return wrapper.innerHTML;

    const slides = root.querySelectorAll('.swiper-slide');
    if (slides.length) {
      return Array.from(slides)
        .map((slide) => slide.outerHTML)
        .join('');
    }

    return root.innerHTML;
  }

  async #fetchSlides(url) {
    if (!url) return null;
    const signal = this.#abortController?.signal;
    const fetchUrl = `${url}${url.includes('?') ? '&' : '?'}t=${Date.now()}`;
    const res = await fetch(fetchUrl, { credentials: 'same-origin', signal });
    if (!res.ok) return null;

    const html = await res.text();
    const temp = document.createElement('div');
    temp.innerHTML = html;

    const root = this.#getSlidesRoot(temp);
    if (!root.querySelector('.swiper-slide')) return null;
    return root;
  }

  #trimSlides(displayLimit) {
    const slides = [...this.#wrapper.querySelectorAll('.swiper-slide')];
    slides.slice(displayLimit).forEach((slide) => slide.remove());
  }

  #initSwiper() {
    delete this.dataset.upsellNavBound;

    try {
      const settings = this.#swiperEl.getAttribute('data-swiper-settings');
      if (!settings) throw new Error('missing data-swiper-settings');

      const config = JSON.parse(settings);
      config.watchOverflow = true;

      if (this.#swiperEl.swiper) {
        this.#swiperEl.swiper.destroy(true, true);
      }

      const swiper = new Swiper(this.#swiperEl, config);
      swiper.on('init', () => syncCartUpsellNavigation(this));
    } catch (e) {
      console.error('<cart-upsell-full>: swiper init failed', e);
      this.#revealAfterLoad();
      return;
    }

    syncCartUpsellNavigation(this);
    bindCartUpsellNavigationSync(this);
    requestAnimationFrame(() => syncCartUpsellNavigation(this));
    this.#revealAfterLoad();
    this.dispatchEvent(new CustomEvent('cart-upsell-full:loaded', { bubbles: true }));
  }

  #countSlides(root) {
    return root?.querySelectorAll('.swiper-slide').length ?? 0;
  }

  #updateFetchConfig(productIds) {
    const ids = [
      ...new Set(
        productIds.map((id) => parseInt(id, 10)).filter((id) => Number.isInteger(id) && id > 0)
      ),
    ];

    this.dataset.productIds = JSON.stringify(ids);

    if (ids.length) {
      const url = this.#buildRecommendationsUrl(ids[0]);
      if (url) this.dataset.url = url;
    }

    return ids;
  }

  async #resolveSlides(productIds, displayLimit) {
    if (!productIds.length) return null;

    let slides = await this.#fetchSlides(this.#buildRecommendationsUrl(productIds[0]));

    if (slides && productIds.length > 1) {
      let slideCount = this.#countSlides(slides);

      for (let i = 1; i < productIds.length && slideCount < displayLimit; i += 1) {
        const more = await this.#fetchSlides(this.#buildRecommendationsUrl(productIds[i]));
        if (!more) continue;
        slideCount = this.#mergeSlides(slides, more, displayLimit);
      }
    }

    if (!slides && productIds.length > 1) {
      for (let i = 1; i < productIds.length; i += 1) {
        slides = await this.#fetchSlides(this.#buildRecommendationsUrl(productIds[i]));
        if (slides) break;
      }
    }

    return slides;
  }

  #applySlides(slides, displayLimit) {
    if (!slides?.querySelector('.swiper-slide')) return false;

    this.#wrapper.innerHTML = this.#getSlideMarkup(slides);
    this.#trimSlides(displayLimit);

    return !!this.#wrapper.querySelector('.swiper-slide');
  }

  #cacheSlide(slide) {
    const id = parseInt(slide.dataset.productId, 10);
    if (!Number.isInteger(id) || id <= 0) return;
    this.#slideCache.set(id, slide.cloneNode(true));
  }

  #restoreCachedSlides(cartProductIds, displayLimit) {
    const cartIds = new Set(
      (Array.isArray(cartProductIds) ? cartProductIds : [])
        .map((id) => parseInt(id, 10))
        .filter((id) => Number.isInteger(id) && id > 0)
    );
    const visibleIds = new Set(
      [...this.#wrapper.querySelectorAll('.swiper-slide')]
        .map((slide) => parseInt(slide.dataset.productId, 10))
        .filter((id) => Number.isInteger(id) && id > 0)
    );

    for (const [productId, slide] of this.#slideCache.entries()) {
      if (cartIds.has(productId) || visibleIds.has(productId)) continue;
      if (this.#wrapper.querySelectorAll('.swiper-slide').length >= displayLimit) break;

      this.#wrapper.appendChild(slide.cloneNode(true));
      visibleIds.add(productId);
    }
  }

  removeSlide(slide) {
    if (!(slide instanceof Element)) return;
    this.#cacheSlide(slide);
    slide.remove();
    this.#updateSwiper();
  }

  async refresh(cartProductIds) {
    if (!this.isConnected || !this.#wrapper || !this.#swiperEl) return;
    if (this.hasAttribute('data-loaded')) return;

    const generation = ++this.#refreshGeneration;
    const ids = this.#updateFetchConfig(
      Array.isArray(cartProductIds) ? cartProductIds : this.#getProductIds()
    );

    if (!ids.length) {
      this.remove();
      return;
    }

    const displayLimit = this.#getDisplayLimit();
    const slides = await this.#resolveSlides(ids, displayLimit);

    if (generation !== this.#refreshGeneration) return;

    if (!this.#applySlides(slides, displayLimit)) {
      this.remove();
      return;
    }

    this.#restoreCachedSlides(ids, displayLimit);

    if (!this.#wrapper.querySelector('.swiper-slide')) {
      this.remove();
      return;
    }

    this.#updateSwiper();
  }

  loadRecommendations(cartProductIds) {
    return this.refresh(cartProductIds);
  }

  #mergeSlides(targetRoot, sourceRoot, displayLimit) {
    const seenIds = new Set(
      [...targetRoot.querySelectorAll('.swiper-slide')]
        .map((slide) => slide.dataset.productId)
        .filter(Boolean)
    );

    let slideCount = seenIds.size;

    for (const slide of sourceRoot.querySelectorAll('.swiper-slide')) {
      if (slideCount >= displayLimit) break;

      const id = slide.dataset.productId;
      if (!id || seenIds.has(id)) continue;

      seenIds.add(id);
      targetRoot.appendChild(slide.cloneNode(true));
      slideCount += 1;
    }

    return slideCount;
  }

  #updateSwiper() {
    const slideCount = this.#wrapper.querySelectorAll('.swiper-slide').length;
    if (!slideCount) return;

    const swiper = this.#swiperEl?.swiper;
    if (swiper) {
      delete this.dataset.upsellNavBound;
      swiper.update();
      syncCartUpsellNavigation(this);
      bindCartUpsellNavigationSync(this);
      return;
    }

    this.#initSwiper();
  }

  async #load() {
    try {
      const displayLimit = this.#getDisplayLimit();
      const productIds = this.#getProductIds();
      const slides = await this.#resolveSlides(productIds, displayLimit);

      if (!this.#applySlides(slides, displayLimit)) {
        this.remove();
        return;
      }

      this.#initSwiper();
    } catch (e) {
      if (e?.name === 'AbortError') return;
      this.remove();
    }
  }
}

if (!customElements.get('cart-upsell-full')) {
  customElements.define('cart-upsell-full', CartUpsellFull);
}

document.addEventListener('swiper:initialized', (event) => {
  const container = event.target?.closest?.('.cart-upsell--empty__container');
  if (!container) return;

  requestAnimationFrame(() => {
    syncCartUpsellNavigation(container);
    bindCartUpsellNavigationSync(container);
  });
});
